import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'

import { lazyWithRetry } from '../../lib/lazyWithRetry.js'
import { isAdminNextEnabled } from '../../lib/config/buildFlags.js'
import { ProtectedLayout } from '../../layouts/ProtectedLayout.jsx'
import { PublicLayout } from '../../layouts/PublicLayout.jsx'
import { AppLayout } from '../../layouts/AppLayout/AppLayout.jsx'
import { AdminLayout } from '../../layouts/AdminLayout.jsx'
import { LanesLayout } from '../../layouts/LanesLayout.jsx'
import { OrderEditorLayout } from '../../layouts/OrderEditorLayout.jsx'
import { HomeRedirect } from './HomeRedirect.jsx'
import {
  StreetTurnsAdminRedirect,
  StreetTurnDetailSuffixRedirect,
} from './StreetTurnsRedirects.jsx'

// Public routes (eagerly loaded)
import { LoginPage } from '../../routes/login.jsx'
import KindeCallback from '../../features/auth/components/KindeCallback'
import { DeeplinkRedirect } from '../DeeplinkRedirect/DeeplinkRedirect.jsx'

// Lazy-loaded page components
const MarketplacePage = lazyWithRetry(() =>
  import('../../routes/marketplace.jsx').then((m) => ({
    default: m.MarketplacePage,
  })),
)
const StreetTurnsListPage = lazyWithRetry(() =>
  import('../../routes/streetturns.jsx').then((m) => ({
    default: m.StreetTurnsListPage,
  })),
)
const StreetTurnDetailPage = lazyWithRetry(() =>
  import('../../routes/streetturn-detail.jsx').then((m) => ({
    default: m.StreetTurnDetailPage,
  })),
)
const ContainersPage = lazyWithRetry(() =>
  import('../../routes/containers.jsx').then((m) => ({
    default: m.ContainersPage,
  })),
)
const LanesPage = lazyWithRetry(() =>
  import('../../routes/lanes.jsx').then((m) => ({
    default: m.LanesPage,
  })),
)
const OrderDetailsPage = lazyWithRetry(() =>
  import('../../routes/order-details.jsx').then((m) => ({
    default: m.OrderDetailsPage,
  })),
)
const OrdersNewPage = lazyWithRetry(() =>
  import('../../routes/orders-new.jsx').then((m) => ({
    default: m.OrdersNewPage,
  })),
)
const OrdersEditorPage = lazyWithRetry(() =>
  import('../../routes/orders-editor.jsx').then((m) => ({
    default: m.OrdersEditorPage,
  })),
)
const OrdersDuplicatePage = lazyWithRetry(() =>
  import('../../routes/orders-duplicate.jsx').then((m) => ({
    default: m.OrdersDuplicatePage,
  })),
)
const AdminPlacesPage = lazyWithRetry(() =>
  import('../../routes/admin/places.jsx').then((m) => ({
    default: m.AdminPlacesPage,
  })),
)
const AdminNotificationsPage = lazyWithRetry(() =>
  import('../../routes/admin/notifications.jsx').then((m) => ({
    default: m.AdminNotificationsPage,
  })),
)
const AdminTranslationsPage = lazyWithRetry(() =>
  import('../../routes/admin/translations.jsx').then((m) => ({
    default: m.AdminTranslationsPage,
  })),
)
const CompaniesPage = lazyWithRetry(() =>
  import('../../routes/admin/companies.jsx').then((m) => ({
    default: m.CompaniesPage,
  })),
)
const UsersPage = lazyWithRetry(() =>
  import('../../routes/admin/users.jsx').then((m) => ({
    default: m.UsersPage,
  })),
)
const MarketsPage = lazyWithRetry(() =>
  import('../../routes/admin/markets.jsx').then((m) => ({
    default: m.MarketsPage,
  })),
)
const MigratedLayout = lazyWithRetry(() =>
  import('../../layouts/MigratedLayout.jsx').then((m) => ({
    default: m.MigratedLayout,
  })),
)
const MuiSandbox = lazyWithRetry(() =>
  import('../../sandbox/mui/MuiSandbox.jsx').then((m) => ({
    default: m.MuiSandbox,
  })),
)

export function AppRoutes() {
  return (
    <Routes>
      {/* Public branch — the unauthenticated world */}
      <Route element={<PublicLayout />}>
        <Route path='/login' element={<LoginPage />} />
        <Route path='/callback' element={<KindeCallback />} />
        <Route path='/deeplink' element={<DeeplinkRedirect />} />
      </Route>

      {/* Protected routes */}
      <Route element={<ProtectedLayout />}>
        <Route element={<AppLayout />}>
          {/* Marketplace */}
          <Route
            path='/marketplace/:market/:filter/:page?'
            element={<MarketplacePage />}
          />

          {/* Street Turns */}
          <Route path='/streetturns' element={<StreetTurnsListPage />} />
          {/* Legacy bundled-tab URL → canonical query-param list */}
          <Route
            path='/streetturns/bundled'
            element={<Navigate to='/streetturns?status=bundled' replace />}
          />
          <Route
            path='/streetturns/:streetTurnId'
            element={<StreetTurnDetailPage />}
          />
          {/* Legacy detail links carried a /:referenceNum suffix — drop it */}
          <Route
            path='/streetturns/:streetTurnId/:referenceNum'
            element={<StreetTurnDetailSuffixRedirect />}
          />

          {/* Containers */}
          <Route path='/containers' element={<ContainersPage />} />

          {/* Lanes */}
          <Route element={<LanesLayout />}>
            <Route path='/lanes/:current' element={<LanesPage />} />
            <Route
              path='/lanes'
              element={<Navigate to='/lanes/active' replace />}
            />
          </Route>

          {/* Order Details */}
          <Route path='/:type/details/:uid' element={<OrderDetailsPage />} />

          {/* Order Forms — admin/imc only */}
          <Route element={<OrderEditorLayout />}>
            <Route path='/order/new' element={<OrdersNewPage />} />
            <Route path='/order/edit/:uid' element={<OrdersEditorPage />} />
            <Route
              path='/order/duplicate/:uid'
              element={<OrdersDuplicatePage />}
            />
          </Route>

          {/* Admin routes */}
          <Route element={<AdminLayout />}>
            {/* Legacy Slack/bookmark deep links → strip /admin prefix */}
            <Route
              path='/admin/streetturns/*'
              element={<StreetTurnsAdminRedirect />}
            />
            <Route path='/admin/places/*' element={<AdminPlacesPage />} />
            <Route
              path='/admin/notifications'
              element={<AdminNotificationsPage />}
            />
            <Route path='/admin/companies/*' element={<CompaniesPage />} />
            <Route path='/admin/users/*' element={<UsersPage />} />
            <Route path='/admin/markets/*' element={<MarketsPage />} />
            <Route
              path='/admin/translations'
              element={<AdminTranslationsPage />}
            />
            <Route
              path='/admin'
              element={<Navigate to='/admin/users' replace />}
            />
          </Route>

          {/* Root redirect */}
          <Route path='/' element={<HomeRedirect />} />

          {/* Catch-all — logs the miss, then redirects home */}
          <Route path='*' element={<HomeRedirect notFound />} />
        </Route>

        {/* Migrated routes render their own shell, with the legacy stylesheet off */}
        {isAdminNextEnabled() && (
          <Route element={<AdminLayout />}>
            <Route element={<MigratedLayout />}>
              <Route path='/admin/next/*' element={<MuiSandbox />} />
            </Route>
          </Route>
        )}
      </Route>
    </Routes>
  )
}
